import { useParams, Link } from 'react-router-dom';
import { BLOGS } from '../data/blogData';
import Navbar from './Navbar';
import Footer from './Footer';
import Seo from './Seo';
import { FaCalendar, FaUser, FaArrowLeft } from 'react-icons/fa';

export function BlogDetail() {
  const { slug } = useParams<{ slug: string }>();
  const blog = BLOGS.find((b) => b.slug === slug);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const blogSchema = blog
    ? {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: blog.title,
        description: blog.excerpt,
        datePublished: blog.date,
        author: {
          '@type': 'Person',
          name: blog.author,
        },
        publisher: {
          '@type': 'Organization',
          name: import.meta.env.VITE_COMPANY_NAME || 'EazyPaisa',
        },
      }
    : undefined;

  return (
    <div className="min-h-screen w-full bg-white flex flex-col">
      {blog && (
        <Seo
          title={blog.metaTitle ?? blog.title}
          description={blog.excerpt}
          path={`/blogs/${blog.slug}`}
          schema={blog.schema ?? blogSchema}
        />
      )}
      <Navbar />

      <main className="flex-1">
        {/* Back Button */}
        <div className="w-full pt-36 pb-4 sm:pt-40 md:pt-44">
          <div className="page-container">
            <Link
              to="/blogs"
              className="inline-flex items-center gap-2 text-[#1F3E33] hover:text-[#152C24] transition-colors font-['Inter'] font-semibold"
            >
              <FaArrowLeft aria-hidden="true" />
              <span>Back to Blog</span>
            </Link>
          </div>
        </div>

        {/* Main Content */}
        <div className="w-full pb-16">
          <div className="page-container">
            {!blog ? (
              <div className="bg-red-50 border-2 border-red-200 rounded-xl p-8 text-center">
                <p className="text-red-600 text-lg font-['Inter']">Blog post not found.</p>
                <Link
                  to="/blogs"
                  className="mt-4 inline-block px-6 py-2.5 bg-[#1F3E33] text-white rounded-full hover:bg-[#152C24] transition-colors font-['Inter'] font-semibold"
                >
                  Back to Blog
                </Link>
              </div>
            ) : (
              <article className="bg-white rounded-2xl border border-[#D3D8D0] shadow-md overflow-hidden">
                <div className="w-full overflow-hidden">
                  <img src={blog.image} alt={blog.title} className="w-full h-auto block" />
                </div>

                <div className="p-6 sm:p-10">
                  <h1 className="heading-font text-3xl sm:text-4xl font-bold text-[#1F3E33] mb-4">
                    {blog.title}
                  </h1>

                  <div className="flex flex-wrap items-center gap-6 text-gray-500 text-sm pb-6 mb-6 border-b-2 border-[#F7F4ED] font-['Inter']">
                    <span className="flex items-center gap-2">
                      <FaUser aria-hidden="true" />
                      {blog.author}
                    </span>
                    <span className="flex items-center gap-2">
                      <FaCalendar aria-hidden="true" />
                      {formatDate(blog.date)}
                    </span>
                  </div>

                  <div
                    className="blog-content"
                    dangerouslySetInnerHTML={{ __html: blog.content }}
                  />
                </div>
              </article>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default BlogDetail;
